import { Button, IconButton, Input, Modal, notify } from '@affine/component';
import { SettingsChip } from '@affine/component/setting-components';
import { UserFriendlyError } from '@affine/error';
import { useI18n } from '@affine/i18n';
import { ResetIcon } from '@blocksuite/icons/rc';
import { useService } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { EmbeddingService } from '../services/embedding';
import {
  fetchIndexedDocStates,
  type IndexedDocState,
} from './embedding-doc-index-api';
import * as styles from './styles.css';

/** Every page of the workspace with its place in the search index. */
export const EmbeddingDocIndexDialog = ({
  open,
  onClose,
  workspaceId,
}: {
  open: boolean;
  onClose: () => void;
  workspaceId: string;
}) => {
  const t = useI18n();
  const embeddingService = useService(EmbeddingService);
  const [docs, setDocs] = useState<IndexedDocState[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [busyIds, setBusyIds] = useState<ReadonlySet<string>>(new Set());

  const load = useCallback(() => {
    setLoading(true);
    fetchIndexedDocStates(workspaceId)
      .then(setDocs)
      .catch((error: unknown) => {
        setDocs([]);
        notify.error({
          title:
            t[
              'com.streamlain.settings.workspace.indexer-embedding.doc-index.load-failed'
            ](),
          message: UserFriendlyError.fromAny(error).message,
        });
      })
      .finally(() => setLoading(false));
  }, [t, workspaceId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const visibleDocs = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return docs;
    return docs.filter(doc => doc.title.toLowerCase().includes(query));
  }, [docs, search]);

  const change = useCallback(
    (docId: string, run: () => Promise<void>) => {
      setBusyIds(current => new Set(current).add(docId));
      run()
        .then(load)
        .catch((error: unknown) => {
          notify.error({
            title:
              t[
                'com.streamlain.settings.workspace.indexer-embedding.doc-index.change-failed'
              ](),
            message: UserFriendlyError.fromAny(error).message,
          });
        })
        .finally(() => {
          setBusyIds(current => {
            const next = new Set(current);
            next.delete(docId);
            return next;
          });
        });
    },
    [load, t]
  );

  const counts = {
    indexed: String(docs.filter(doc => doc.indexed && !doc.excluded).length),
    total: String(docs.filter(doc => !doc.excluded).length),
    excluded: String(docs.filter(doc => doc.excluded).length),
  };

  return (
    <Modal
      open={open}
      onOpenChange={(next: boolean) => {
        if (!next) onClose();
      }}
      title={t[
        'com.streamlain.settings.workspace.indexer-embedding.doc-index.title'
      ]()}
      width={560}
    >
      <div className={styles.indexSummary}>
        {loading
          ? t['com.streamlain.loading']()
          : counts.excluded === '0'
            ? t[
                'com.streamlain.settings.workspace.indexer-embedding.doc-index.count'
              ](counts)
            : t[
                'com.streamlain.settings.workspace.indexer-embedding.doc-index.count-excluded'
              ](counts)}
      </div>
      <Input
        value={search}
        onChange={setSearch}
        placeholder={t[
          'com.streamlain.settings.workspace.indexer-embedding.doc-index.search'
        ]()}
        autoFocus
      />
      <div className={styles.indexList}>
        {visibleDocs.length === 0 && !loading ? (
          <div className={styles.indexEmpty}>
            {search.trim()
              ? t[
                  'com.streamlain.settings.workspace.indexer-embedding.doc-index.no-match'
                ]()
              : t[
                  'com.streamlain.settings.workspace.indexer-embedding.doc-index.empty'
                ]()}
          </div>
        ) : null}
        {visibleDocs.map(doc => {
          const busy = busyIds.has(doc.id);
          const title = doc.title || t['Untitled']();
          return (
            <div
              key={doc.id}
              className={styles.indexRow}
              data-excluded={doc.excluded || undefined}
            >
              <span className={styles.docTitle} title={title}>
                <span className={styles.docTitleText}>{title}</span>
              </span>
              <SettingsChip
                tone={doc.excluded ? 'grey' : doc.indexed ? 'green' : 'orange'}
              >
                {doc.excluded
                  ? t[
                      'com.streamlain.settings.workspace.indexer-embedding.doc-index.status.excluded'
                    ]()
                  : doc.indexed
                    ? t[
                        'com.streamlain.settings.workspace.indexer-embedding.doc-index.status.indexed'
                      ]()
                    : t[
                        'com.streamlain.settings.workspace.indexer-embedding.doc-index.status.pending'
                      ]()}
              </SettingsChip>
              {doc.excluded ? null : (
                <IconButton
                  aria-label={t[
                    'com.streamlain.settings.workspace.indexer-embedding.doc-index.reindex-doc'
                  ]()}
                  disabled={busy}
                  onClick={() =>
                    change(doc.id, () => embeddingService.reindexDocs([doc.id]))
                  }
                >
                  <ResetIcon />
                </IconButton>
              )}
              <Button
                variant="plain"
                disabled={busy}
                onClick={() =>
                  change(doc.id, () =>
                    doc.excluded
                      ? embeddingService.updateIgnoredDocs([], [doc.id])
                      : embeddingService.updateIgnoredDocs([doc.id], [])
                  )
                }
              >
                {doc.excluded
                  ? t[
                      'com.streamlain.settings.workspace.indexer-embedding.doc-index.include'
                    ]()
                  : t[
                      'com.streamlain.settings.workspace.indexer-embedding.doc-index.exclude'
                    ]()}
              </Button>
            </div>
          );
        })}
      </div>
    </Modal>
  );
};
